import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";
import { Table, Card, Form, Row, Col, Button, InputGroup, FormControl, Badge } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown, FaCalendarAlt, FaSearch, FaCloudDownloadAlt, FaHdd } from 'react-icons/fa';
import reportService from "../services/report.service";

const SyncBandwidth = () => {
  // Set default date range to last 3 months
  const getDefaultDates = () => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(threeMonthsAgo),
      endDate: formatDate(today)
    };
  };

  const defaultDates = getDefaultDates();
  const [loading, setLoading] = useState(false);
  const [bandwidthData, setBandwidthData] = useState([]);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");

  // Search and Pagination States
  const [searchName, setSearchName] = useState("");
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Sorting states
  const [orderBy, setOrderBy] = useState("totalDownloadSizeGB");
  const [sortDirection, setSortDirection] = useState("DESC");

  const { user: currentUser } = useSelector((state) => state.auth);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!currentUser.roles.includes("ROLE_ADMIN")) {
    return (
      <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
        <div className="alert alert-danger">
          <h4>Unauthorized Access</h4>
          <p>You must be an administrator to view this page.</p>
        </div>
      </div>
    );
  }

  // Date picker validation
  const isDateRangeInvalid = startDate && endDate && new Date(endDate) < new Date(startDate);

  const handleStartDateChange = (val) => {
    setStartDate(val);
    if (val) {
      const parts = val.split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const lastDay = new Date(year, month, 0);
        const yyyy = lastDay.getFullYear();
        const mm = String(lastDay.getMonth() + 1).padStart(2, '0');
        const dd = String(lastDay.getDate()).padStart(2, '0');
        setEndDate(`${yyyy}-${mm}-${dd}`);
      }
    }
  };

  const handleCalculate = async (e) => {
    if (e) e.preventDefault();

    if (!startDate || !endDate) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please select both start and end dates" });
      return;
    }

    if (isDateRangeInvalid) {
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Validation Error: End date must be greater than or equal to start date." });
      return;
    }

    setLoading(true);
    setError(null);
    setSearchInitiated(true);
    setPage(0);
    
    try {
      const response = await reportService.getSyncBandwidth(startDate, endDate, timezone, searchName);
      setBandwidthData(response.data || []);
      if ((response.data || []).length === 0) {
        toast.info("No data found for the selected date range");
      }
    } catch (err) {
      const errorMessage =
        (err.response && err.response.data) ||
        err.message ||
        "Failed to calculate sync bandwidth";
      setError(errorMessage);
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to calculate sync bandwidth" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearReport = () => {
    const dates = getDefaultDates();
    setStartDate(dates.startDate);
    setEndDate(dates.endDate);
    setBandwidthData([]);
    setSearchInitiated(false);
    setSearchName("");
    setPage(0);
    setError(null);
  };

  const formatGB = (gb) => {
    if (gb === null || gb === undefined) return "—";
    return gb.toFixed(2);
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setSortDirection(prev => prev === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(field);
      setSortDirection("ASC");
    }
    setPage(0);
  };

  const renderSortIcon = (field) => {
    if (orderBy !== field) {
      return <FaSort className="ms-1 text-white-50" style={{ opacity: 0.6 }} />;
    }
    return sortDirection === "ASC" 
      ? <FaSortUp className="ms-1 text-warning" />
      : <FaSortDown className="ms-1 text-warning" />;
  };

  const getSortedClients = () => {
    if (!bandwidthData) return [];
    return [...bandwidthData].sort((a, b) => {
      let valA = a[orderBy];
      let valB = b[orderBy];
      
      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";
      
      if (orderBy === "totalDownloadSizeGB" || orderBy === "downloadCount") {
        return sortDirection === "ASC" ? valA - valB : valB - valA;
      }
      
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      
      if (strA < strB) return sortDirection === "ASC" ? -1 : 1;
      if (strA > strB) return sortDirection === "ASC" ? 1 : -1;
      return 0;
    });
  };

  const sortedClients = getSortedClients();
  const totalCount = sortedClients.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const paginatedClients = sortedClients.slice(page * pageSize, (page + 1) * pageSize);

  const handleNextPage = () => {
    if (page + 1 < totalPages) {
      setPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(prev => prev - 1);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
      <style>{`
        .sortable-device-header {
          transition: color 0.15s ease-in-out;
        }
        .sortable-device-header:hover {
          color: #ffc107 !important;
        }
      `}</style>
      <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
          <div className="d-flex align-items-center">
            <FaCloudDownloadAlt size={22} className="me-2 text-warning" />
            <h4 className="mb-0 fw-semibold">Sync Bandwidth</h4>
          </div>
          <Badge bg="secondary" className="px-3 py-2 fs-6">
            Active Timezone: {timezone}
          </Badge>
        </Card.Header>
        <Card.Body className="p-4">
          <p className="text-muted mb-4">
            Calculate total download size per client for a date range
          </p>

          {/* Date Picker + Search Filter Panel */}
          <Form onSubmit={handleCalculate} className="mb-4">
            <Row className="g-3 align-items-end">
              <Col lg={3} md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-2">
                    <FaCalendarAlt className="me-1" /> Start Date
                  </Form.Label>
                  <Form.Control 
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    disabled={loading}
                  />
                </Form.Group>
              </Col>

              <Col lg={3} md={6}>
                <Form.Group className="position-relative">
                  <Form.Label className="fw-semibold text-muted mb-2">
                    <FaCalendarAlt className="me-1" /> End Date
                  </Form.Label>
                  <Form.Control 
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={loading}
                    isInvalid={isDateRangeInvalid}
                  />
                  <Form.Control.Feedback type="invalid" className="position-absolute" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                    End date must be >= start date.
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col lg={4} md={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted mb-2">
                    <FaSearch className="me-1" /> Client Name / ID (optional)
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white border-end-0">
                      <FaSearch className="text-muted" />
                    </InputGroup.Text>
                    <FormControl 
                      placeholder="Filter by client name or ID..."
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      disabled={loading}
                      className="border-start-0 ps-0"
                    />
                    {searchName && (
                      <Button 
                        variant="outline-secondary" 
                        onClick={() => setSearchName("")}
                        disabled={loading}
                      >
                        ✕
                      </Button>
                    )}
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col lg={2} className="d-flex justify-content-lg-end justify-content-start mt-3 mt-lg-0">
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="md"
                  disabled={loading || isDateRangeInvalid}
                  className="me-2 px-3 fw-semibold"
                >
                  {loading ? "Running..." : "Run"}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  size="md"
                  onClick={handleClearReport}
                  disabled={loading}
                  className="px-3 fw-semibold"
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Mini Pagination (Only visible when report loaded) */}
          {searchInitiated && !loading && bandwidthData.length > 0 && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mb-3 pt-3 border-top">
              <span className="text-muted fs-6">
                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
              </span>
              <div className="d-flex align-items-center">
                <span className="me-3 text-muted fs-6">
                  Page <strong>{page + 1}</strong> of {Math.max(1, totalPages)}
                </span>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="me-2 px-3 fw-semibold" 
                  onClick={handlePrevPage} 
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline-primary" 
                  size="sm" 
                  className="px-3 fw-semibold" 
                  onClick={handleNextPage} 
                  disabled={page + 1 >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mt-4" role="alert">
              {error}
            </div>
          )}

          {/* Master Table */}
          <div className="table-responsive border rounded-3 overflow-hidden mt-3">
            <Table hover className="mb-0 align-middle">
              <thead className="table-dark">
                <tr>
                  <th 
                    onClick={() => handleSort("clientId")} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="py-3 px-4 fw-semibold text-white sortable-device-header"
                  >
                    Client ID {renderSortIcon("clientId")}
                  </th>
                  <th 
                    onClick={() => handleSort("clientName")} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="py-3 px-4 fw-semibold text-white sortable-device-header"
                  >
                    Client Name {renderSortIcon("clientName")}
                  </th>
                  <th 
                    onClick={() => handleSort("totalDownloadSizeGB")} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="py-3 px-3 fw-semibold text-white text-center sortable-device-header"
                  >
                    Download Size (GB) {renderSortIcon("totalDownloadSizeGB")}
                  </th>
                  <th 
                    onClick={() => handleSort("downloadCount")} 
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    className="py-3 px-3 fw-semibold text-white text-center sortable-device-header"
                  >
                    Download Count {renderSortIcon("downloadCount")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {!searchInitiated ? (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                      <FaHdd size={42} className="mb-3 text-secondary d-block mx-auto" />
                      <h5 className="text-dark fw-bold">Ready to Load Sync Bandwidth Report</h5>
                      <p className="mb-0 text-muted fs-6">Select a date range above and click <strong>Run</strong> to load sync bandwidth statistics.</p>
                    </td>
                  </tr>
                ) : loading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-5">
                      <div className="spinner-border text-primary me-2" role="status"></div>
                      <span className="text-muted fw-semibold fs-5">Calculating sync bandwidth...</span>
                    </td>
                  </tr>
                ) : paginatedClients.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted fw-semibold">
                      No sync bandwidth recorded for the selected period/filter.
                    </td>
                  </tr>
                ) : (
                  paginatedClients.map((item, index) => (
                    <tr key={item.clientId || index} style={{ transition: 'background-color 0.2s' }}>
                      <td className="px-4 text-dark monospace" style={{ fontSize: '0.9rem' }}>
                        {item.clientId || "—"}
                      </td>
                      <td className="px-4 fw-semibold text-dark fs-6">
                        {item.clientName || "—"}
                      </td>
                      <td className="px-3 text-center">
                        <Badge bg={item.totalDownloadSizeGB > 0 ? "success" : "light"} className={item.totalDownloadSizeGB > 0 ? "px-3 py-2 fs-6 text-white" : "px-3 py-2 fs-6 text-dark border"}>
                          {formatGB(item.totalDownloadSizeGB)} GB
                        </Badge>
                      </td>
                      <td className="px-3 text-center text-dark fw-medium fs-6">
                        {item.downloadCount?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Overview summary cards */}
          {searchInitiated && !loading && bandwidthData.length > 0 && (
            <div className="row mt-4">
              <div className="col-md-6 mb-3">
                <Card className="bg-light border-0 shadow-xs">
                  <Card.Body className="text-center py-3">
                    <h6 className="text-muted mb-1">Total Download Size</h6>
                    <h3 className="mb-0 fw-bold text-dark">
                      {formatGB(bandwidthData.reduce((sum, item) => sum + (item.totalDownloadSizeGB || 0), 0))} GB
                    </h3>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-6 mb-3">
                <Card className="bg-light border-0 shadow-xs">
                  <Card.Body className="text-center py-3">
                    <h6 className="text-muted mb-1">Total Download Count</h6>
                    <h3 className="mb-0 fw-bold text-dark">
                      {bandwidthData.reduce((sum, item) => sum + (item.downloadCount || 0), 0).toLocaleString()}
                    </h3>
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}

          {/* Pagination display footer */}
          {searchInitiated && !loading && totalCount > 0 && (
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4">
              <span className="text-muted mb-3 mb-sm-0">
                Showing <strong>{paginatedClients.length}</strong> of <strong>{totalCount}</strong> clients
              </span>
              {totalPages > 1 && (
                <div className="d-flex align-items-center">
                  <Button 
                    variant="outline-secondary" 
                    className="me-2 fw-semibold"
                    onClick={() => setPage(0)}
                    disabled={page === 0}
                  >
                    First
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    className="me-2 fw-semibold"
                    onClick={handlePrevPage} 
                    disabled={page === 0}
                  >
                    Prev
                  </Button>
                  <span className="mx-2 font-weight-bold">
                    Page <strong>{page + 1}</strong> of {totalPages}
                  </span>
                  <Button 
                    variant="outline-secondary" 
                    className="ms-2 me-2 fw-semibold"
                    onClick={handleNextPage} 
                    disabled={page + 1 >= totalPages}
                  >
                    Next
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    className="fw-semibold"
                    onClick={() => setPage(totalPages - 1)}
                    disabled={page + 1 >= totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          )}

        </Card.Body>
      </Card>
    </div>
  );
};

export default SyncBandwidth;
